import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useConversationStore } from '@/stores/conversationStore';
import { SEARCH_DEBOUNCE_MS, SEARCH_PREFIX, isSearchFolder, searchFolderId, searchQueryOf } from './folders';

/** `/conversations` and `/conversations/<id>`: the screens that LIST conversations, where the search view lives. */
const onConversationsPath = (pathname: string): boolean => pathname === '/conversations' || pathname.startsWith('/conversations/');

/**
 * ADR-033 / PR-C3 (C3-1) - the top bar's search, wired to the URL.
 *
 * <b>The search is a folder-shaped VIEW</b>: `?folder=search:<q>` on `/conversations`. Everything after the URL is the
 * ordinary folder machinery (ConversationsPage parses it, the shell's keyset loader pages it, the list renders it), so
 * there is no second list mechanism and a refresh or the back button lands on the same search.
 *
 * <b>What this hook owns is only the typing side:</b>
 *  - the TEXT lives in the conversation store (`searchQuery`), which forgets it on a tenant/agent change;
 *  - on a conversations screen the view follows the text {@link SEARCH_DEBOUNCE_MS} ms after the LAST keystroke, and only
 *    once the trimmed text is long enough ({@link searchFolderId}: one character sends NOTHING);
 *  - on any other screen typing alone navigates nowhere - Enter does (it commits at once);
 *  - emptying the box leaves the search view at once (no debounce: stopping needs no restraint), returning to the folder the
 *    agent was in before (kept in a ref; the default folder when the search was opened by a link);
 *  - leaving the search view by ANY other route (a folder in the rail, the back button) empties the box, and arriving at a
 *    search view that the box did not produce (a reload, a shared link, the back button) fills it - the box and the view
 *    never disagree. A search is a snapshot: its pages are forgotten when it is left.
 *
 * The query only ever travels inside a URL search parameter (encoded by URLSearchParams); it is never placed in a pattern
 * or a DOM string here.
 */
export function useSearchNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const text = useConversationStore((s) => s.searchQuery);
  const setText = useConversationStore((s) => s.setSearchQuery);

  const onConversations = onConversationsPath(location.pathname);
  const folderParam = new URLSearchParams(location.search).get('folder');
  // The search view the URL shows right now (normalised), or null: not on a conversations screen, no folder, or not a search.
  const urlQuery = onConversations && folderParam !== null && isSearchFolder(folderParam) ? searchQueryOf(folderParam) : null;

  // Every callback reads the CURRENT render through this ref, so a debounce timer started two renders ago never acts on a
  // stale location.
  const latest = useRef({ location, onConversations, urlQuery, text });
  latest.current = { location, onConversations, urlQuery, text };

  /** The query the box last put into the URL (or the one the URL last put into the box); null = no search view of ours. */
  const committedRef = useRef<string | null>(null);
  /** The `?folder=` the agent had before the search view opened (null = none, i.e. the default folder). */
  const previousFolderRef = useRef<string | null>(null);
  /** This hook is about to leave the search view itself, so the arrival must not empty what the agent is still typing. */
  const leavingRef = useRef(false);
  const wasSearchRef = useRef(false);

  /** Open (or update) the search view for `raw` now. Below the minimum it does nothing at all. */
  const commit = useCallback(
    (raw: string) => {
      const id = searchFolderId(raw);
      if (id === null) return;
      const q = id.slice(SEARCH_PREFIX.length);
      const { location: loc, onConversations: here, urlQuery: shown } = latest.current;
      if (shown === q && committedRef.current === q) return;
      committedRef.current = q;
      const next = new URLSearchParams(here ? loc.search : '');
      next.set('folder', id);
      navigate(
        { pathname: here ? loc.pathname : '/conversations', search: `?${next.toString()}` },
        // The first keystroke-driven entry is a history step; refining the query is not (back goes to where the agent was).
        { replace: shown !== null },
      );
    },
    [navigate],
  );

  /** Leave the search view for the folder the agent was in. A no-op when no search view is showing. */
  const leave = useCallback(() => {
    const { location: loc, urlQuery: shown } = latest.current;
    committedRef.current = null;
    if (shown === null) return;
    leavingRef.current = true;
    const next = new URLSearchParams(loc.search);
    const previous = previousFolderRef.current;
    if (previous) next.set('folder', previous);
    else next.delete('folder');
    const search = next.toString();
    navigate({ pathname: loc.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [navigate]);

  // The typing side: the view follows the text WHEN THE TEXT CHANGES (a mount, or a URL that arrives with an empty box, is
  // not typing). Blank leaves at once; anything else waits for the last keystroke.
  const handledTextRef = useRef(text);
  useEffect(() => {
    if (text === handledTextRef.current) return undefined;
    if (!latest.current.onConversations) return undefined;
    if (text.trim().length === 0) {
      handledTextRef.current = text;
      leave();
      return undefined;
    }
    const timer = setTimeout(() => {
      const current = latest.current.text;
      handledTextRef.current = current;
      if (searchFolderId(current) !== null) commit(current);
      else leave();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, commit, leave]);

  // The URL side: keep the box and the view in agreement when the URL moved for a reason other than typing.
  useEffect(() => {
    if (urlQuery !== null) {
      if (urlQuery !== committedRef.current) {
        committedRef.current = urlQuery;
        if (latest.current.text !== urlQuery) setText(urlQuery);
      }
      wasSearchRef.current = true;
      return;
    }
    if (wasSearchRef.current) useConversationStore.getState().forgetSearchPages();
    wasSearchRef.current = false;
    if (leavingRef.current) {
      // We left it ourselves because the text got short: what is left in the box is still being typed.
      leavingRef.current = false;
    } else {
      committedRef.current = null;
      if (latest.current.text !== '') setText('');
    }
    // Remember where the agent was, so ending a search can take them back.
    if (onConversations) previousFolderRef.current = folderParam;
    // Only a change of location matters here; the values are derived from it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  /** Enter in the box: commit now (the only way to search from a screen that does not list conversations). */
  const submit = useCallback(() => commit(latest.current.text), [commit]);

  /** The box's clear button / Escape: empty the text and leave the search view now. */
  const clear = useCallback(() => {
    setText('');
    leave();
  }, [setText, leave]);

  return { text, setText, submit, clear, searching: urlQuery !== null };
}
